import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';

/** Subset of `<model-viewer>` used for camera-orbit animation. */
type ModelViewerCamera = HTMLElement & {
  cameraOrbit: string;
  readonly loaded: boolean;
};

type MascotMode = 'svg' | 'model';

/** GLB mascot for AI chrome (header + assistant card). Chat meta row stays SVG-only. */
@Component({
  selector: 'app-robocat-mascot-model',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './robocat-mascot-model.component.html',
  styleUrl: './robocat-mascot-model.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RobocatMascotModelComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly modelViewer = viewChild<ElementRef<ModelViewerCamera>>('robocatMv');

  /** SVG until WebGL is confirmed and model-viewer is registered. */
  readonly mascotMode = signal<MascotMode>('svg');

  constructor() {
    let intersectionObserver: IntersectionObserver | undefined;
    let stopAnimationLoop: (() => void) | undefined;

    const useSvgFallback = () => {
      stopAnimationLoop?.();
      intersectionObserver?.disconnect();
      intersectionObserver = undefined;
      this.mascotMode.set('svg');
    };

    this.destroyRef.onDestroy(() => {
      intersectionObserver?.disconnect();
      stopAnimationLoop?.();
    });

    effect(() => {
      if (this.mascotMode() !== 'model') {
        return;
      }

      queueMicrotask(() => {
        requestAnimationFrame(() => {
          stopAnimationLoop = this.setupModelAnimation({
            useSvgFallback,
            setIntersectionObserver: (observer) => {
              intersectionObserver = observer;
            },
          });
        });
      });
    });

    afterNextRender(() => {
      if (!isWebGlAvailable()) {
        return;
      }

      void import('@google/model-viewer')
        .then(() => {
          this.mascotMode.set('model');
        })
        .catch(() => {
          useSvgFallback();
        });
    });
  }

  private setupModelAnimation(hooks: {
    useSvgFallback: () => void;
    setIntersectionObserver: (observer: IntersectionObserver | undefined) => void;
  }): () => void {
    if (globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return () => undefined;
    }

    let rafId = 0;
    let active = false;

    const stopLocalLoop = () => {
      active = false;
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
    };

    requestAnimationFrame(() => {
      const el = this.modelViewer()?.nativeElement;
      if (!el) {
        hooks.useSvgFallback();
        return;
      }

      el.addEventListener('error', hooks.useSvgFallback, { once: true });

      const startLoop = () => {
        stopLocalLoop();

        const orbit = restingOrbit(el.cameraOrbit);
        const basePolarDeg = orbit.polarDeg;
        const baseRadius = orbit.radius;

        /** Slow horizontal look (~16s per full left–right–left). */
        const azimuthPeriodMs = 16_000;
        const maxAzimuthDeg = 36;

        /** Slower “lean in” — polar + slight zoom, ~23s so it rarely lines up with azimuth. */
        const leanPeriodMs = 23_000;
        const polarLeanDeg = 7;
        const radiusLeanFactor = 0.055;

        active = true;

        const loop = (now: number) => {
          if (!active || !el.isConnected || !el.loaded) {
            rafId = 0;
            return;
          }

          const azimuth =
            Math.sin((now / azimuthPeriodMs) * Math.PI * 2) * maxAzimuthDeg;

          const leanPhase = (now / leanPeriodMs) * Math.PI * 2 + 0.9;
          const lean = Math.sin(leanPhase);
          const polarDeg = clamp(basePolarDeg + lean * polarLeanDeg, 58, 92);
          const radiusOut = formatRadius(
            baseRadius,
            1 - lean * radiusLeanFactor,
          );

          try {
            el.cameraOrbit = `${azimuth.toFixed(1)}deg ${polarDeg.toFixed(1)}deg ${radiusOut}`;
          } catch {
            stopLocalLoop();
            return;
          }

          rafId = requestAnimationFrame(loop);
        };

        rafId = requestAnimationFrame(loop);
      };

      const scheduleStart = () => {
        requestAnimationFrame(() => requestAnimationFrame(startLoop));
      };

      const onVisible = (entries: IntersectionObserverEntry[]) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible) {
          if (el.loaded) {
            scheduleStart();
          } else {
            el.addEventListener('load', scheduleStart, { once: true });
          }
        } else {
          stopLocalLoop();
        }
      };

      if (typeof IntersectionObserver === 'undefined') {
        if (el.loaded) {
          scheduleStart();
        } else {
          el.addEventListener('load', scheduleStart, { once: true });
        }
        return;
      }

      const observer = new IntersectionObserver(onVisible, {
        threshold: 0.05,
      });
      observer.observe(el);
      hooks.setIntersectionObserver(observer);
    });

    return stopLocalLoop;
  }
}

function isWebGlAvailable(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  try {
    const canvas = document.createElement('canvas');
    const contextIds = ['webgl2', 'webgl', 'experimental-webgl'] as const;

    for (const id of contextIds) {
      const gl = canvas.getContext(id, {
        failIfMajorPerformanceCaveat: false,
      });
      if (gl) {
        return true;
      }
    }
  } catch {
    return false;
  }

  return false;
}

type RadiusToken =
  | { kind: 'pct'; value: number }
  | { kind: 'm'; value: number }
  | { kind: 'raw'; text: string };

function restingOrbit(cameraOrbit: string): {
  polarDeg: number;
  radius: RadiusToken;
} {
  const parts = cameraOrbit.trim().split(/\s+/).filter(Boolean);
  const polarDeg =
    parts.length >= 2 ? angleTokenToDeg(parts[1]!) : 75;
  const radius: RadiusToken =
    parts.length >= 3 ? parseRadiusToken(parts[2]!) : { kind: 'pct', value: 105 };
  return { polarDeg, radius };
}

function angleTokenToDeg(token: string): number {
  const t = token.trim().toLowerCase();
  if (t.endsWith('deg')) {
    return parseFloat(t);
  }
  if (t.endsWith('rad')) {
    return (parseFloat(t) * 180) / Math.PI;
  }
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : 75;
}

function parseRadiusToken(token: string): RadiusToken {
  const t = token.trim().toLowerCase();
  if (t.endsWith('%')) {
    return { kind: 'pct', value: parseFloat(t) };
  }
  if (t.endsWith('m')) {
    return { kind: 'm', value: parseFloat(t) };
  }
  return { kind: 'raw', text: token.trim() };
}

function formatRadius(base: RadiusToken, scale: number): string {
  if (base.kind === 'pct') {
    const v = clamp(base.value * scale, 88, 120);
    return `${v.toFixed(1)}%`;
  }
  if (base.kind === 'm') {
    const v = clamp(base.value * scale, 0.4, 8);
    return `${v.toFixed(2)}m`;
  }
  return base.text;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}
